import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useSocket } from '../../context/SocketContext';
import { useToast } from '../../context/ToastContext';
import SwipeCard from './SwipeCard';
import SurveyCard from './SurveyCard';
import { UndoIcon, XIcon, StarIcon, HeartIcon } from '../../utils/icons';

const SWIPE_THRESHOLD = 80;
const SURVEY_INTERVAL = 10; // Show survey every N swipes

const styles = {
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  stackWrapper: {
    position: 'absolute',
    inset: '10px',
  },
  actions: {
    position: 'absolute',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '16px',
    zIndex: 20,
  },
  actionBtn: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    border: '2px solid',
    background: 'var(--bg-surface)',
    cursor: 'pointer',
    transition: 'transform 100ms ease, all 200ms',
    boxShadow: 'var(--shadow)',
  },
  matchOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    animation: 'fadeIn 300ms ease',
  },
  matchTitle: {
    fontSize: '36px',
    fontWeight: '800',
    background: 'linear-gradient(135deg, var(--accent), #f7a)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'matchPulse 500ms ease',
    marginBottom: '12px',
  },
  matchSubtitle: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: '32px',
  },
};

export default function CardStack({ filters, onOpenFilters }) {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [animatingSwipe, setAnimatingSwipe] = useState(null);
  const [matchInfo, setMatchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swipeCount, setSwipeCount] = useState(0);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyTraits, setSurveyTraits] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [pressedBtn, setPressedBtn] = useState(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const { joinMatch } = useSocket();
  const { showToast } = useToast();

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters?.min_age) params.set('min_age', filters.min_age);
      if (filters?.max_age) params.set('max_age', filters.max_age);
      if (filters?.max_distance) params.set('max_distance', filters.max_distance);
      if (filters?.gender_pref?.length) {
        params.set('gender_pref', filters.gender_pref.join(','));
      }
      if (filters?.vaccine_pref?.length) {
        params.set('vaccine_pref', JSON.stringify(filters.vaccine_pref));
      }

      const res = await api.get(`/discover/feed?${params.toString()}`);
      setProfiles(res.data);
      setCurrentIndex(0);
    } catch {
      // ignore
      showToast('Failed to load profiles', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Fetch survey traits from the server
  const fetchSurveyTraits = async () => {
    setSurveyLoading(true);
    try {
      const res = await api.get('/discover/survey');
      setSurveyTraits(res.data.traits);
    } catch (err) {
      console.error('Failed to fetch survey:', err);
      setSurveyTraits(null);
    } finally {
      setSurveyLoading(false);
    }
  };

  const handleSwipe = async (action) => {
    const profile = profiles[currentIndex];
    if (!profile || animatingSwipe) return;

    setAnimatingSwipe(action);

    try {
      const res = await api.post('/discover/swipe', {
        swiped_id: profile.id,
        action,
      });

      if (res.data.match) {
        setMatchInfo({
          name: res.data.matched_user || profile.display_name,
          matchId: res.data.match_id,
        });
        joinMatch(res.data.match_id);
        navigator.vibrate?.(200);
      }

      const nextSwipeCount = swipeCount + 1;
      setSwipeCount(nextSwipeCount);

      setTimeout(() => {
        setCurrentIndex((i) => i + 1);
        setDragPos({ x: 0, y: 0 });
        setAnimatingSwipe(null);

        // Show survey card every SURVEY_INTERVAL swipes
        if (nextSwipeCount > 0 && nextSwipeCount % SURVEY_INTERVAL === 0) {
          fetchSurveyTraits();
          setShowSurvey(true);
        }

        // Fetch more if running low
        if (currentIndex >= profiles.length - 3) {
          fetchProfiles();
        }
      }, 300);
    } catch {
      // ignore
      showToast('Swipe failed — try again', 'error');
      setDragPos({ x: 0, y: 0 });
      setAnimatingSwipe(null);
    }
  };

  const handleSurveyComplete = () => {
    setShowSurvey(false);
    setSurveyTraits(null);
  };

  const handleSurveySkip = () => {
    setShowSurvey(false);
    setSurveyTraits(null);
  };

  const handleUndo = async () => {
    try {
      await api.post('/discover/undo');
      fetchProfiles();
    } catch {
      // ignore
      showToast('Undo failed', 'error');
    }
  };

  // Drag handlers
  const handleDragStart = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = { x: clientX, y: clientY };
    setIsDragging(true);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragPos({
      x: clientX - dragStartRef.current.x,
      y: clientY - dragStartRef.current.y,
    });
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (dragPos.x > SWIPE_THRESHOLD) {
      handleSwipe('like');
    } else if (dragPos.x < -SWIPE_THRESHOLD) {
      handleSwipe('pass');
    } else {
      setDragPos({ x: 0, y: 0 });
    }
  };

  // If survey is shown but traits failed to load, auto-dismiss
  useEffect(() => {
    if (showSurvey && !surveyLoading && !surveyTraits) {
      handleSurveySkip();
    }
  }, [showSurvey, surveyLoading, surveyTraits]);

  const currentProfile = profiles[currentIndex];
  const nextProfile = profiles[currentIndex + 1];

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: 16, overflow: 'hidden', background: 'var(--bg-surface)' }}>
          <div className="skeleton" style={{ width: '100%', height: '65%' }} />
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="skeleton" style={{ width: '60%', height: 20 }} />
            <div className="skeleton" style={{ width: '40%', height: 14 }} />
            <div className="skeleton" style={{ width: '80%', height: 14 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div style={styles.container}>
        <div className="empty-state" style={{ height: '100%' }}>
          <div className="empty-state-icon">◇</div>
          <h3 className="empty-state-title">No more profiles</h3>
          <p>Check back later or adjust your filters</p>
          <button
            className="btn btn-primary"
            onClick={fetchProfiles}
            style={{ marginTop: '16px' }}
          >
            Refresh
          </button>
          <button onClick={() => navigate('/wall')} className="btn btn-secondary" style={{ marginTop: 8 }}>
            Try the Wall →
          </button>
          <button onClick={onOpenFilters} className="btn btn-ghost" style={{ marginTop: 4 }}>
            Adjust Filters
          </button>
        </div>
      </div>
    );
  }

  // ──── Survey Card Mode ────
  if (showSurvey && (surveyLoading || surveyTraits)) {
    return (
      <div style={styles.container}>
        <div style={styles.stackWrapper}>
          {surveyLoading ? (
            <div className="loading-spinner" style={{ height: '100%' }}>
              <div className="spinner" />
            </div>
          ) : (
            <SurveyCard
              traits={surveyTraits}
              onComplete={handleSurveyComplete}
              onSkip={handleSurveySkip}
            />
          )}
        </div>
      </div>
    );
  }

  // ──── Normal Swipe Card Mode ────
  const rotation = dragPos.x * 0.1;
  const cardStyle = animatingSwipe
    ? {
        transition: 'transform 300ms ease, opacity 300ms ease',
        transform: `translateX(${animatingSwipe === 'pass' ? '-150%' : '150%'}) rotate(${animatingSwipe === 'pass' ? '-20deg' : '20deg'})`,
        opacity: 0,
      }
    : {
        transition: isDragging ? 'none' : 'transform 300ms ease',
        transform: `translate(${dragPos.x}px, ${dragPos.y}px) rotate(${rotation}deg)`,
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: 10,
      };

  return (
    <div style={styles.container}>
      <div
        style={styles.stackWrapper}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
      >
        {/* Next card (behind) */}
        {nextProfile && (
          <SwipeCard
            profile={nextProfile}
            style={{
              transform: 'scale(0.95)',
              opacity: 0.5,
              zIndex: 5,
            }}
          />
        )}

        {/* Current card */}
        <SwipeCard
          profile={currentProfile}
          style={cardStyle}
          dragOffset={dragPos.x}
        />
      </div>

      {/* Action buttons */}
      <div style={styles.actions}>
        <button
          style={{
            ...styles.actionBtn,
            borderColor: 'var(--text-muted)',
            color: 'var(--text-muted)',
            ...(pressedBtn === 'undo' ? { transform: 'scale(0.9)' } : {}),
          }}
          onClick={handleUndo}
          onTouchStart={() => setPressedBtn('undo')}
          onTouchEnd={() => setPressedBtn(null)}
          title="Undo"
        >
          <UndoIcon size={20} />
        </button>
        <button
          style={{
            ...styles.actionBtn,
            borderColor: 'var(--danger)',
            color: 'var(--danger)',
            ...(pressedBtn === 'pass' ? { transform: 'scale(0.9)' } : {}),
          }}
          onClick={() => handleSwipe('pass')}
          onTouchStart={() => setPressedBtn('pass')}
          onTouchEnd={() => setPressedBtn(null)}
          title="Pass"
        >
          <XIcon size={24} />
        </button>
        <button
          style={{
            ...styles.actionBtn,
            borderColor: 'var(--super-like)',
            color: 'var(--super-like)',
            ...(pressedBtn === 'super_like' ? { transform: 'scale(0.9)' } : {}),
          }}
          onClick={() => handleSwipe('super_like')}
          onTouchStart={() => setPressedBtn('super_like')}
          onTouchEnd={() => setPressedBtn(null)}
          title="Super Like"
        >
          <StarIcon size={22} />
        </button>
        <button
          style={{
            ...styles.actionBtn,
            borderColor: 'var(--success)',
            color: 'var(--success)',
            ...(pressedBtn === 'like' ? { transform: 'scale(0.9)' } : {}),
          }}
          onClick={() => handleSwipe('like')}
          onTouchStart={() => setPressedBtn('like')}
          onTouchEnd={() => setPressedBtn(null)}
          title="Like"
        >
          <HeartIcon size={24} />
        </button>
      </div>

      {/* Match overlay */}
      {matchInfo && (
        <div style={styles.matchOverlay} onClick={() => setMatchInfo(null)}>
          {/* Confetti particles */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            {[...Array(12)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                top: -10,
                left: `${8 + (i * 7.5)}%`,
                width: 8,
                height: 8,
                borderRadius: i % 3 === 0 ? '50%' : 2,
                background: ['#E8576D', '#FFD700', '#86efac', '#93c5fd', '#c084fc', '#fb923c'][i % 6],
                animation: `confettiFall ${1.5 + (i * 0.15)}s ease-in forwards`,
                animationDelay: `${i * 0.1}s`,
                opacity: 0,
              }} />
            ))}
          </div>
          <div style={{ animation: 'heartPulse 1s ease-in-out infinite' }}>
            <h2 style={styles.matchTitle}>It&apos;s a Match!</h2>
          </div>
          <p style={styles.matchSubtitle}>
            You and {matchInfo.name} liked each other
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn btn-primary"
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = `/chat/${matchInfo.matchId}`;
              }}
            >
              Send a message
            </button>
            <button
              className="btn btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                setMatchInfo(null);
              }}
            >
              Keep swiping
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
